import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  hotel_name: "",
  contact_name: "",
  email: "",
  phone: "",
  parking_spaces: "",
  avg_cars_per_night: "",
  notes: "",
};

export const ApplicationForm = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submittedHotel, setSubmittedHotel] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, {
        ...form,
        parking_spaces: Number(form.parking_spaces),
        avg_cars_per_night: Number(form.avg_cars_per_night),
        notes: form.notes || null,
      });
      setSubmittedHotel(form.hotel_name);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedHotel) {
    return (
      <motion.div
        data-testid="thank-you-screen"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-white rounded-lg border border-border p-10 md:p-14 text-center shadow-sm"
      >
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-blue-50">
          <PartyPopper className="h-8 w-8 text-accent" />
        </div>
        <h3 className="font-serif text-3xl md:text-4xl tracking-tight text-primary mb-4">
          Thank you, {submittedHotel}.
        </h3>
        <p className="text-base md:text-lg text-muted-foreground max-w-md mx-auto leading-relaxed">
          Your pilot application is in. We review every application personally and
          will be in touch within two working days to confirm your pilot dates.
        </p>
        <div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-accent" />
          <span>Application received — no card, no commitment.</span>
        </div>
      </motion.div>
    );
  }

  return (
    <form
      data-testid="pilot-application-form"
      onSubmit={handleSubmit}
      className="bg-white rounded-lg border border-border p-8 md:p-10 shadow-sm space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="hotel_name">Hotel name</Label>
          <Input
            id="hotel_name"
            data-testid="hotel-name-input"
            required
            minLength={2}
            placeholder="e.g. The Kensington Suites"
            value={form.hotel_name}
            onChange={set("hotel_name")}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact_name">Your name</Label>
          <Input
            id="contact_name"
            data-testid="contact-name-input"
            required
            minLength={2}
            placeholder="e.g. Priya Shah"
            value={form.contact_name}
            onChange={set("contact_name")}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            data-testid="phone-input"
            required
            minLength={5}
            type="tel"
            placeholder="e.g. 020 7946 0000"
            value={form.phone}
            onChange={set("phone")}
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="email">Work email</Label>
          <Input
            id="email"
            data-testid="email-input"
            required
            type="email"
            placeholder="you@yourhotel.co.uk"
            value={form.email}
            onChange={set("email")}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="parking_spaces">On-site parking spaces</Label>
          <Input
            id="parking_spaces"
            data-testid="parking-spaces-input"
            required
            type="number"
            min={1}
            placeholder="e.g. 40"
            value={form.parking_spaces}
            onChange={set("parking_spaces")}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="avg_cars_per_night">Average cars per night</Label>
          <Input
            id="avg_cars_per_night"
            data-testid="avg-cars-input"
            required
            type="number"
            min={0}
            placeholder="e.g. 25"
            value={form.avg_cars_per_night}
            onChange={set("avg_cars_per_night")}
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="notes">Anything else? (optional)</Label>
          <Input
            id="notes"
            data-testid="notes-input"
            placeholder="e.g. underground car park, mostly leisure guests"
            value={form.notes}
            onChange={set("notes")}
          />
        </div>
      </div>
      <Button
        data-testid="submit-application-btn"
        type="submit"
        disabled={submitting}
        className="w-full h-12 rounded-full bg-accent text-accent-foreground text-base font-semibold hover:bg-blue-700 transition-colors"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
          </>
        ) : (
          "Apply for free pilot"
        )}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        No card details. No commitment. We reply within two working days.
      </p>
    </form>
  );
};
